import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";
import { isAdminProfileDeleted } from "@/lib/api";
import {
  adminUserSubscriptionStatusFromDetail,
  kycStatusLabelFr,
  litigeRowStatusLabelFr,
  negotiationStatusLabelFr,
  dossierStatusLabelFr,
  profileRoleLabelFr,
} from "@/lib/marketplaceStatusLabels";
import {
  ChevronDown,
  ChevronRight,
  PauseCircle,
  Trash2,
  AlertTriangle,
} from "lucide-react";

function asList(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object" && Array.isArray(v.items)) return v.items;
  if (v && typeof v === "object" && Array.isArray(v.data)) return v.data;
  return [];
}

function adminTxStatutLabelFr(st) {
  const s = String(st || "").trim().toLowerCase();
  const map = {
    paye: "Séquestre",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Décision requise",
    fonds_debloques: "RDV confirmé",
    rembourse: "Remboursé",
    annule: "Annulé",
  };
  return map[s] || (s ? String(st).trim() : "—");
}

function tabLabelWithCount(label, count) {
  const n = Number(count) || 0;
  return n > 0 ? `${label} (${n})` : label;
}

function activityKindLabelFr(kind) {
  const map = {
    account: "Compte",
    dossier: "Annonce",
    transaction: "Transaction",
    transaction_update: "Transaction",
    negotiation: "Négociation",
    litige_open: "Litige",
    litige_resolve: "Litige",
    notation: "Note",
  };
  return map[String(kind || "").trim()] || kind || "—";
}

function PeerLink({ userId, label, onOpenUser }) {
  if (!userId || !label) return <span className="text-slate-400">—</span>;
  return (
    <button
      type="button"
      className="font-medium text-[#2D7FF9] hover:underline text-left"
      onClick={(e) => {
        e.stopPropagation();
        onOpenUser?.(userId);
      }}
    >
      {label}
    </button>
  );
}

function SummaryChip({ label, badgeClass, children, title }) {
  return (
    <div
      className="flex min-w-[7.5rem] flex-1 flex-col gap-1 rounded-lg border border-slate-200/90 bg-white px-3 py-2.5 shadow-sm"
      title={title}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="flex flex-wrap items-center gap-1">{children}</div>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-100 py-2.5 last:border-0 sm:flex-row sm:items-baseline sm:gap-4 sm:py-2">
      <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:w-28">
        {label}
      </dt>
      <dd className="min-w-0 text-sm text-slate-900 break-words">{children}</dd>
    </div>
  );
}

function SectionBlock({ title, count, isEmpty, emptyLabel, children }) {
  return (
    <section className="min-w-0">
      <h4 className="mb-2 text-sm font-semibold text-slate-900">
        {title}
        {count != null ? <span className="ml-1.5 font-normal text-slate-500">({count})</span> : null}
      </h4>
      {isEmpty ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-4 text-xs text-slate-500">
          {emptyLabel}
        </p>
      ) : (
        children
      )}
    </section>
  );
}

function RowCard({ onClick, disabled, children, testId }) {
  const Tag = onClick && !disabled ? "button" : "div";
  return (
    <Tag
      type={Tag === "button" ? "button" : undefined}
      onClick={onClick && !disabled ? onClick : undefined}
      data-testid={testId}
      className={cn(
        "w-full min-w-0 rounded-lg border border-slate-200/90 bg-white px-3 py-2.5 text-left text-xs shadow-sm",
        onClick && !disabled && "cursor-pointer transition-colors hover:border-[#2D7FF9]/30 hover:bg-sky-50/50",
      )}
    >
      {children}
    </Tag>
  );
}

function QualitySummary({ sellerQuality, buyerQuality }) {
  const [open, setOpen] = useState(false);
  if (!sellerQuality && !buyerQuality) return null;

  const sellerLine = sellerQuality?.has_seller_activity
    ? `${sellerQuality.seller_transaction_count} vente(s) · ${sellerQuality.seller_litiges_count} litige(s)`
    : "Pas d’activité vendeur";
  const buyerLine = buyerQuality?.has_buyer_activity
    ? `${buyerQuality.buyer_transaction_count} achat(s) · ${buyerQuality.buyer_litiges_count} litige(s)`
    : "Pas d’activité acheteur";
  const alerts = [...(sellerQuality?.alerts || []), ...(buyerQuality?.alerts || [])];

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-xl border border-slate-200 bg-slate-50/80">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-800 hover:bg-slate-100/80">
        <span>Indicateurs vendeur / acheteur</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-500 transition-transform", open && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 border-t border-slate-200/80 px-3 pb-3 pt-2">
        <p className="text-xs text-slate-600">
          <strong>Vendeur :</strong> {sellerLine}
          <br />
          <strong>Acheteur :</strong> {buyerLine}
        </p>
        {alerts.length > 0 ? (
          <ul className="space-y-1.5">
            {alerts.map((a, i) => (
              <li
                key={i}
                className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-950"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />
                {a.text}
              </li>
            ))}
          </ul>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

function accountStatusBadge(summary, profileDeleted) {
  if (profileDeleted) {
    return <Badge className="bg-slate-700 text-white text-xs">Supprimé</Badge>;
  }
  if (summary.is_suspended) {
    return <Badge className="bg-amber-600 text-white text-xs">Suspendu</Badge>;
  }
  if (summary.banned) {
    return <Badge className="bg-red-600 text-white text-xs">Bloqué</Badge>;
  }
  return <Badge className="bg-emerald-100 text-emerald-800 text-xs font-normal">Actif</Badge>;
}

/** Barre Suspendre / Supprimer — fixée sous le titre de la modale. */
export function AdminUserDetailToolbar({ summary, userId, onAccountAction }) {
  const profileDeleted = isAdminProfileDeleted(summary);
  const canModerate = summary?.role !== "admin" && !profileDeleted;
  if (!canModerate) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {summary.is_suspended ? (
        <Button size="sm" variant="outline" type="button" onClick={() => onAccountAction?.(userId, "unsuspend")}>
          Réactiver
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 text-amber-900 hover:bg-amber-50"
          type="button"
          onClick={() => onAccountAction?.(userId, "suspend")}
        >
          <PauseCircle className="mr-1 h-4 w-4" />
          Suspendre
        </Button>
      )}
      <Button size="sm" variant="destructive" type="button" onClick={() => onAccountAction?.(userId, "delete")}>
        <Trash2 className="mr-1 h-4 w-4" />
        Supprimer
      </Button>
    </div>
  );
}

export default function AdminUserDetailPanel({
  detail,
  userId,
  sellerQuality,
  buyerQuality,
  formatAdminDateTime,
  onOpenDossier,
  onOpenLitige,
  onOpenUser,
  onActivityClick,
}) {
  const summary = detail?.summary || {};
  const transactions = asList(detail?.transactions);
  const dossiers = asList(detail?.dossiers);
  const litiges = asList(detail?.litiges);
  const negotiations = asList(detail?.negotiations);
  const activity = asList(detail?.activity);

  const profileDeleted = isAdminProfileDeleted(summary);
  const subscriptionStatus = adminUserSubscriptionStatusFromDetail(detail);
  const sub = detail?.subscription;

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <SummaryChip label="Abonnement" title={subscriptionStatus.title}>
          <Badge className={cn("text-xs font-normal", subscriptionStatus.badgeClass)}>
            {subscriptionStatus.label}
          </Badge>
        </SummaryChip>
        <SummaryChip label="Compte">
          <div className="flex flex-wrap items-center gap-1">
            {accountStatusBadge(summary, profileDeleted)}
            {summary.report_count > 0 ? (
              <Badge className="bg-orange-100 text-orange-800 text-xs font-normal">
                {summary.report_count} sign.
              </Badge>
            ) : null}
          </div>
        </SummaryChip>
        <SummaryChip label="Rôle">
          <span className="text-sm font-medium text-slate-900">{profileRoleLabelFr(summary.role)}</span>
        </SummaryChip>
      </div>

      <dl className="rounded-xl border border-slate-200/90 bg-slate-50/50 px-3 sm:px-4">
        <MetaRow label="Téléphone">
          {summary.phone ? (
            <a href={`tel:${String(summary.phone).replace(/\s/g, "")}`} className="text-[#2D7FF9] hover:underline">
              {formatPhoneForDisplay(summary.phone)}
            </a>
          ) : (
            "—"
          )}
        </MetaRow>
        <MetaRow label="Société">{summary.company_name || "—"}</MetaRow>
        <MetaRow label="Inscription">
          {detail?.profile?.created_at ? formatAdminDateTime(detail.profile.created_at) : "—"}
        </MetaRow>
        {sub?.current_period_end ? (
          <MetaRow label="Fin abo">{formatAdminDateTime(sub.current_period_end)}</MetaRow>
        ) : null}
        {sub?.canceled_at ? (
          <MetaRow label="Résiliation abo">{formatAdminDateTime(sub.canceled_at)}</MetaRow>
        ) : null}
        {summary.deleted_at ? (
          <MetaRow label="Compte supprimé">{formatAdminDateTime(summary.deleted_at)}</MetaRow>
        ) : null}
        {summary.siret ? <MetaRow label="SIRET">{summary.siret}</MetaRow> : null}
        <MetaRow label="Identité (KYC)">
          <span className="text-slate-600">{kycStatusLabelFr(summary.kyc_status)}</span>
        </MetaRow>
      </dl>

      <QualitySummary sellerQuality={sellerQuality} buyerQuality={buyerQuality} />

      <Tabs defaultValue="activity" className="min-w-0">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-slate-100/90 p-1 sm:grid-cols-5">
          <TabsTrigger value="activity" className="text-xs data-[state=active]:bg-white sm:text-sm">
            Activité
          </TabsTrigger>
          <TabsTrigger value="transactions" className="text-xs data-[state=active]:bg-white sm:text-sm">
            {tabLabelWithCount("Trans.", transactions.length)}
          </TabsTrigger>
          <TabsTrigger value="annonces" className="text-xs data-[state=active]:bg-white sm:text-sm">
            {tabLabelWithCount("Annonces", dossiers.length)}
          </TabsTrigger>
          <TabsTrigger value="litiges" className="text-xs data-[state=active]:bg-white sm:text-sm">
            {tabLabelWithCount("Litiges", litiges.length)}
          </TabsTrigger>
          <TabsTrigger value="negos" className="col-span-2 text-xs data-[state=active]:bg-white sm:col-span-1 sm:text-sm">
            {tabLabelWithCount("Négos", negotiations.length)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="mt-3 min-w-0 focus-visible:outline-none">
          <SectionBlock title="Historique" isEmpty={activity.length === 0} emptyLabel="Aucun événement.">
            <ul className="max-h-[min(42vh,360px)] space-y-2 overflow-y-auto pr-0.5">
              {activity.map((a) => {
                const hasLink = Boolean(a.link_dossier_id || a.link_litige_id || a.link_profile_id);
                return (
                  <li key={a.id}>
                    <RowCard
                      onClick={hasLink ? () => onActivityClick?.(a) : undefined}
                      disabled={!hasLink}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="tabular-nums text-slate-500">{formatAdminDateTime(a.at)}</span>
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                          {activityKindLabelFr(a.kind)}
                        </Badge>
                        {hasLink ? <ChevronRight className="ml-auto h-3.5 w-3.5 text-slate-400" /> : null}
                      </div>
                      <p className="mt-1 font-medium text-slate-800">{a.title}</p>
                      {a.subtitle ? <p className="mt-0.5 text-slate-600 line-clamp-2">{a.subtitle}</p> : null}
                    </RowCard>
                  </li>
                );
              })}
            </ul>
          </SectionBlock>
        </TabsContent>

        <TabsContent value="transactions" className="mt-3 min-w-0 focus-visible:outline-none">
          <SectionBlock title="Transactions" isEmpty={transactions.length === 0} emptyLabel="Aucune transaction.">
            <ul className="max-h-[min(42vh,360px)] space-y-2 overflow-y-auto">
              {transactions.map((t) => {
                const role = t.buyer_id === userId ? "Acheteur" : "Vendeur";
                const prix = t.price ?? t.buyer_total;
                return (
                  <li key={t.id}>
                    <RowCard
                      onClick={t.dossier_id ? () => onOpenDossier?.(String(t.dossier_id)) : undefined}
                      disabled={!t.dossier_id}
                    >
                      <p className="font-semibold text-slate-900 line-clamp-2">{t.dossier_title || "Annonce"}</p>
                      <p className="mt-1 text-slate-600">
                        {formatAdminDateTime(t.created_at)} · {role} · {adminTxStatutLabelFr(t.statut)}
                        {prix != null && Number.isFinite(Number(prix)) ? (
                          <span className="font-semibold text-slate-900"> · {Number(prix)} €</span>
                        ) : null}
                      </p>
                      <p className="mt-1 text-slate-500">
                        Avec{" "}
                        <PeerLink userId={t.counterpart_user_id} label={t.counterpart_label} onOpenUser={onOpenUser} />
                      </p>
                    </RowCard>
                  </li>
                );
              })}
            </ul>
          </SectionBlock>
        </TabsContent>

        <TabsContent value="annonces" className="mt-3 min-w-0 focus-visible:outline-none">
          <SectionBlock title="Annonces vendeur" isEmpty={dossiers.length === 0} emptyLabel="Aucune annonce.">
            <ul className="max-h-[min(42vh,360px)] space-y-2 overflow-y-auto">
              {dossiers.map((d) => (
                <li key={d.id}>
                  <RowCard onClick={() => onOpenDossier?.(String(d.id))}>
                    <p className="font-semibold text-slate-900 line-clamp-2">{d.titre?.trim() || "Sans titre"}</p>
                    <p className="mt-1 text-slate-600">{dossierStatusLabelFr(d.status)}</p>
                  </RowCard>
                </li>
              ))}
            </ul>
          </SectionBlock>
        </TabsContent>

        <TabsContent value="litiges" className="mt-3 min-w-0 focus-visible:outline-none">
          <SectionBlock title="Litiges" isEmpty={litiges.length === 0} emptyLabel="Aucun litige.">
            <ul className="max-h-[min(42vh,360px)] space-y-2 overflow-y-auto">
              {litiges.map((l) => {
                const litigePk = l.id || l.dispute_id;
                return (
                  <li key={litigePk}>
                    <RowCard
                      onClick={litigePk ? () => onOpenLitige?.(String(litigePk)) : undefined}
                      disabled={!litigePk}
                      testId={litigePk ? `admin-user-litige-open-${litigePk}` : undefined}
                    >
                      <p className="font-semibold text-slate-900 line-clamp-2">{l.dossier_title || "Litige"}</p>
                      <p className="mt-1 text-slate-600">
                        {formatAdminDateTime(l.created_at)} · {litigeRowStatusLabelFr(l.status ?? l.statut)}
                      </p>
                      {l.reason ? <p className="mt-1 text-slate-500 line-clamp-2">Motif : {l.reason}</p> : null}
                      {(l.buyer_label || l.seller_label) && (
                        <p className="mt-1 text-slate-500 line-clamp-1">
                          {[l.buyer_label, l.seller_label].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </RowCard>
                  </li>
                );
              })}
            </ul>
          </SectionBlock>
        </TabsContent>

        <TabsContent value="negos" className="mt-3 min-w-0 focus-visible:outline-none">
          <SectionBlock title="Négociations" isEmpty={negotiations.length === 0} emptyLabel="Aucune négociation.">
            <ul className="max-h-[min(42vh,360px)] space-y-2 overflow-y-auto">
              {negotiations.map((n) => {
                const role = n.buyer_id === userId ? "Acheteur" : "Vendeur";
                return (
                  <li key={n.id}>
                    <RowCard
                      onClick={n.dossier_id ? () => onOpenDossier?.(String(n.dossier_id)) : undefined}
                      disabled={!n.dossier_id}
                    >
                      <p className="font-semibold text-slate-900 line-clamp-2">{n.dossier_title || "Annonce"}</p>
                      <p className="mt-1 text-slate-600">
                        {formatAdminDateTime(n.created_at)} · {role} · {negotiationStatusLabelFr(n.status)}
                      </p>
                      <p className="mt-1 text-slate-500">
                        Avec{" "}
                        <PeerLink userId={n.counterpart_user_id} label={n.counterpart_label} onOpenUser={onOpenUser} />
                      </p>
                    </RowCard>
                  </li>
                );
              })}
            </ul>
          </SectionBlock>
        </TabsContent>
      </Tabs>
    </div>
  );
}
